import { useState } from 'react';
import { ChevronDown, ChevronUp, Clock, Trophy } from '@/utils/icons';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { InfoTooltip } from '@/components/common/InfoTooltip';
import { getUptimeColor } from '@/utils/calculations';
import { cn } from '@/lib/utils';
import type { SingleMetricStats } from '@/types/metrics';
import { PeriodStatsRow } from '@/components/common/PeriodStatsRow';

interface NetworkUptimeStats {
  avgUptimeDays: number;
  longestUptimeDays: number;
  longestUptimeNodeName: string | null;
  shortestUptimeDays: number;
  shortestUptimeNodeName: string | null;
}

interface UptimeCardProps {
  uptime: number;
  label?: string;
  networkUptimeStats?: NetworkUptimeStats;
  expanded?: boolean;
  onToggleExpand?: () => void;
  periodStats?: SingleMetricStats;
  periodStatsLoading?: boolean;
}

function formatDays(days: number) {
  if (days >= 1) return `${Math.floor(days)} days`;
  const hours = days * 24;
  if (hours >= 1) return `${Math.floor(hours)} hours`;
  return `${Math.floor(hours * 60)} min`;
}

export function UptimeCard({
  uptime,
  label = 'Current Uptime',
  networkUptimeStats,
  expanded: controlledExpanded,
  onToggleExpand,
  periodStats,
  periodStatsLoading,
}: UptimeCardProps) {
  const [internalExpanded, setInternalExpanded] = useState(false);
  const expanded = controlledExpanded ?? internalExpanded;
  const toggleExpand = onToggleExpand ?? (() => setInternalExpanded(!internalExpanded));
  const colorClass = getUptimeColor(uptime);

  return (
    <Card>
      <CardHeader className="dls-px-4 dls-pb-2">
        <CardTitle className="dls-text-sm dls-font-medium dls-text-muted-foreground dls-flex dls-items-center dls-gap-1.5 dls-whitespace-nowrap">
          {label}
          <InfoTooltip text="Percentage of monitored Nodes currently reporting an ALIVE state. Click to see network uptime details." />
          {networkUptimeStats && (
            <button
              type="button"
              className="dls-ml-auto dls-text-muted-foreground hover:dls-text-foreground dls-transition-colors"
              onClick={(e) => { e.stopPropagation(); toggleExpand(); }}
            >
              {expanded ? <ChevronUp className="dls-h-4 dls-w-4" /> : <ChevronDown className="dls-h-4 dls-w-4" />}
            </button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="dls-px-4 dls-pb-3">
        <div
          role={networkUptimeStats ? 'button' : undefined}
          tabIndex={networkUptimeStats ? 0 : undefined}
          onClick={networkUptimeStats ? () => toggleExpand() : undefined}
          onKeyDown={
            networkUptimeStats
              ? (e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleExpand();
                  }
                }
              : undefined
          }
          className={cn('dls-w-full dls-text-left dls-min-h-20', networkUptimeStats && 'dls-cursor-pointer')}
        >
          <div>
            <span className={cn('dls-text-4xl dls-font-bold', colorClass)}>
              {uptime.toFixed(1)}<span className="dls-text-lg">%</span>
            </span>
            {periodStats && (
              <div className="dls-mt-1">
                <PeriodStatsRow
                  stats={periodStats}
                  format={(v) => `${v.toFixed(1)}%`}
                  loading={periodStatsLoading}
                />
              </div>
            )}
          </div>
        </div>

        {expanded && networkUptimeStats && (
          <div className="dls-mt-3 dls-pt-3 dls-border-t dls-space-y-3 dls-max-h-[300px] dls-overflow-y-auto">
            <p className="dls-text-sm dls-font-semibold dls-text-muted-foreground dls-flex dls-items-center dls-gap-1">
              Network
              <InfoTooltip text="Time since each node last transitioned to alive. Measures continuous network connectivity — resets when a node goes offline and comes back, even without a reboot." />
            </p>
            {networkUptimeStats.longestUptimeNodeName && (
              <div className="dls-flex dls-items-start dls-gap-3">
                <Trophy className="dls-h-4 dls-w-4 dls-text-status-warning dls-shrink-0 dls-mt-0.5" />
                <div>
                  <p className="dls-text-xs dls-text-muted-foreground">Longest Uptime</p>
                  <p className="dls-text-sm dls-font-semibold">
                    {formatDays(networkUptimeStats.longestUptimeDays)}
                  </p>
                  <p className="dls-text-xs dls-text-muted-foreground">
                    {networkUptimeStats.longestUptimeNodeName}
                  </p>
                </div>
              </div>
            )}
            <div className="dls-flex dls-items-start dls-gap-3">
              <Clock className="dls-h-4 dls-w-4 dls-text-muted-foreground dls-shrink-0 dls-mt-0.5" />
              <div>
                <p className="dls-text-xs dls-text-muted-foreground">Average Uptime</p>
                <p className="dls-text-lg dls-font-bold">{formatDays(networkUptimeStats.avgUptimeDays)}</p>
              </div>
            </div>
            {networkUptimeStats.shortestUptimeNodeName && (
              <div className="dls-flex dls-items-start dls-gap-3">
                <Clock className="dls-h-4 dls-w-4 dls-text-status-info dls-shrink-0 dls-mt-0.5" />
                <div>
                  <p className="dls-text-xs dls-text-muted-foreground">Shortest Uptime</p>
                  <p className="dls-text-sm dls-font-semibold">
                    {formatDays(networkUptimeStats.shortestUptimeDays)}
                  </p>
                  <p className="dls-text-xs dls-text-muted-foreground">
                    {networkUptimeStats.shortestUptimeNodeName}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
